import { useState } from "react";
import {
  Bot,
  Gauge,
  ShieldCheck,
  Code2,
  FileUp,
  SlidersHorizontal,
  LineChart,
  Radar,
  ListChecks,
  KeyRound,
  TrendingUp,
  FileText,
  PlayCircle,
  Workflow,
} from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { PageHero, SectionHeading, ScreenshotFrame, Button, HeroShots } from "../common/ui";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/TestLabs.css";
import performanceShot from "../../../assets/product/performance-create-script.webp";
import securityShot from "../../../assets/product/security-dashboard.webp";
import codeShot from "../../../assets/product/code-analyser.webp";
import automationShot from "../../../assets/product/automation-script-ai.webp";
import FrothAiIcon from "../common/FrothAiIcon";

const labs = [
  {
    id: "performance",
    label: "Performance Test Lab",
    icon: Gauge,
    image: performanceShot,
    alt: "Create Test Script screen with Live Recorder and HAR file upload",
    text: [
      "Find out how your application behaves under load before your users do. Capture real user journeys with the TestOps Recorder browser extension or upload HAR files, or build multi-step API workflows.",
      "Froth TestOps generates Locust load scripts for you automatically, then runs them with the user load, ramp-up and duration you choose.",
    ],
    tags: ["Locust", "HAR upload", "Live Recorder", "API Workflow Studio"],
    cards: [
      {
        icon: FileUp,
        title: "Record or import",
        text: "Record journeys in the browser, upload HAR files, or build API workflows with variables, authentication and data files.",
      },
      {
        icon: SlidersHorizontal,
        title: "Simple or advanced load tests",
        text: "Set user load, ramp-up and duration, and define response-time and error limits for each run.",
      },
      {
        icon: LineChart,
        title: "Clear reports",
        text: "Response times, requests per second, users over time, error ratios and a full HTML report, downloadable as CSV or HTML.",
      },
    ],
  },
  {
    id: "security",
    label: "Security Test Lab",
    icon: ShieldCheck,
    image: securityShot,
    alt: "Security Test Lab dashboard with alert level, alerts by severity and targets summary",
    text: [
      "Scan your web applications for vulnerabilities with OWASP ZAP-based security testing. Register internal or external targets, run a quick scan or schedule regular scans, and track your Cyber Hygiene Score over time.",
    ],
    tags: ["OWASP ZAP", "Quick & scheduled scans", "Cyber Hygiene Score"],
    cards: [
      {
        icon: Radar,
        title: "Quick and scheduled scans",
        text: "Choose safe, protected, normal or attack mode, run on demand, or schedule scans daily, weekly or monthly.",
      },
      {
        icon: ListChecks,
        title: "Findings you can act on",
        text: "Issues are grouped by risk (critical to informational), with occurrences tracked across scans.",
      },
      {
        icon: FrothAiIcon,
        title: "AI security reports",
        accent: true,
        text: "Generate an AI-written scan report, or build your own report and save it as a template.",
      },
    ],
  },
  {
    id: "code",
    label: "Code Analyzer",
    icon: Code2,
    image: codeShot,
    alt: "Code Analyzer dashboard with scan status overview, top vulnerabilities by severity and recent activity",
    text: [
      "Catch security issues in code before it ships. Connect your GitHub or GitLab repositories and scan them for vulnerable code with Semgrep, mapped to OWASP Top 10 and CWE, and for leaked keys and tokens with Gitleaks.",
    ],
    tags: ["Semgrep SAST", "Gitleaks secret scanning", "OWASP Top 10 & CWE", "GitHub & GitLab"],
    cards: [
      {
        icon: KeyRound,
        title: "SAST and secret scanning",
        text: "Scan on demand or on a schedule, with rulesets such as OWASP Top 10 and security audit.",
      },
      {
        icon: TrendingUp,
        title: "Trends at a glance",
        text: "Severity trends, scan status, top vulnerabilities and secrets by repository on one dashboard.",
      },
      {
        icon: FileText,
        title: "AI code reports",
        accent: true,
        text: "Export findings with file, line, CWE and recommendation, or generate an AI report for your team.",
      },
    ],
  },
  {
    id: "automation",
    label: "Automation Test Lab",
    icon: Bot,
    image: automationShot,
    alt: "Automation script screen with FrothAI-generated Playwright and Appium scripts",
    text: [
      "Cut out the repetitive manual runs. Record journeys with the TestOps Recorder browser extension, or let FrothAI generate Playwright and Appium scripts straight from your reviewed test cases.",
      "Run your scripts on your own CI runners or on BrowserStack browsers and devices, and keep every result next to the test cases and requirements they came from.",
    ],
    tags: ["TestOps Recorder", "Playwright", "Appium", "CI runners & BrowserStack"],
    cards: [
      {
        icon: PlayCircle,
        title: "Record or generate",
        text: "Record steps in the browser, or let FrothAI turn your reviewed test cases into Playwright and Appium scripts.",
      },
      {
        icon: Workflow,
        title: "Run on your infrastructure",
        text: "Execute unattended on your own CI runners or on BrowserStack devices, scheduled from the platform.",
      },
      {
        icon: FrothAiIcon,
        accent: true,
        title: "AI automation scripts",
        text: "FrothAI writes Playwright and Appium scripts from your test cases, so automation coverage grows with every test you add.",
      },
    ],
  },
];

export default function TestLabs() {
  const [active, setActive] = useState(labs[0].id);
  const lab = labs.find(l => l.id === active);
  const labIndex = labs.findIndex(l => l.id === active);
  const LabIcon = lab.icon;

  // Roving tabindex: arrows step through the lab rack (wrapping), Home/End jump.
  const onKeyDown = e => {
    const steps = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    let next;
    if (e.key in steps) next = (labIndex + steps[e.key] + labs.length) % labs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = labs.length - 1;
    else return;
    e.preventDefault();
    setActive(labs[next].id);
    document.getElementById(`tab-${labs[next].id}`)?.focus();
  };

  return (
    <>
      <SEOHead
        title="Performance, Security, Code & Automation Testing Labs | Froth TestOps"
        description="Load test with Locust, scan applications with OWASP ZAP, analyse code with Semgrep and Gitleaks, and automate with Playwright and Appium, all inside Froth TestOps."
        keywords="performance testing, load testing, Locust, security testing, OWASP ZAP, SAST, secret scanning, test automation, Playwright, Appium, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <Header />
        <main>
          <PageHero
            crumbs="Test Labs"
            eyebrow="Platform"
            title={
              <>
                Test Labs: Performance, Security, Code and <span>Automation</span>
              </>
            }
            lead="Quality is more than functional testing. Froth TestOps brings performance, security, code and automation testing into the same platform as your test management, so every release is checked from every angle and everything is reported in one place."
            actions={
              <>
                <Button to="/contact" icon>
                  Talk to Us
                </Button>
                <Button to="/froth-ai" variant="ghost">
                  See FrothAI
                </Button>
              </>
            }
            visual={
              <HeroShots
                main={securityShot}
                mainAlt="Froth TestOps Security Test Lab dashboard"
                float={codeShot}
                floatAlt="Froth TestOps Code Analyzer"
              />
            }
          />

          {/* Lab bench console: rack rail + blueprint screen + readout rows. */}
          <section className="ft-section">
            <div className="ft-container">
              <div className="tlb-console">
                <div
                  className="tlb-rail"
                  role="tablist"
                  aria-label="Test Labs"
                  aria-orientation="vertical"
                  onKeyDown={onKeyDown}
                >
                  {labs.map((l, i) => {
                    const Icon = l.icon;
                    return (
                      <button
                        key={l.id}
                        type="button"
                        role="tab"
                        id={`tab-${l.id}`}
                        aria-selected={active === l.id}
                        aria-controls={`panel-${l.id}`}
                        tabIndex={active === l.id ? 0 : -1}
                        className="tlb-module"
                        onClick={() => setActive(l.id)}
                      >
                        <span className="tlb-module__id" aria-hidden="true">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="tlb-module__icon">
                          <Icon size={18} aria-hidden="true" />
                        </span>
                        <span className="tlb-module__label">{l.label}</span>
                        <span className="tlb-module__led" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>

                <div
                  className="tlb-bench"
                  key={lab.id}
                  role="tabpanel"
                  id={`panel-${lab.id}`}
                  aria-labelledby={`tab-${lab.id}`}
                >
                  <div className="tlb-screen">
                    <div className="tlb-screen__bar">
                      <span className="tlb-screen__code">LAB/{String(labIndex + 1).padStart(2, "0")}</span>
                      <span className="tlb-screen__fig" aria-hidden="true">
                        FIG 0{labIndex + 1}
                      </span>
                      <span className="tlb-screen__led" aria-hidden="true" />
                    </div>
                    <ScreenshotFrame src={lab.image} alt={lab.alt} />
                  </div>

                  <div className="tlb-readout">
                    <h2 className="tlb-readout__title">
                      <LabIcon size={26} aria-hidden="true" />
                      {lab.label}
                    </h2>
                    {lab.text.map(t => (
                      <p className="tlb-text" key={t}>
                        {t}
                      </p>
                    ))}
                    <div className="tlb-tags">
                      {lab.tags.map(t => (
                        <span className="tlb-tag" key={t}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="tlb-rows">
                    {lab.cards.map(c => {
                      const Icon = c.icon;
                      return (
                        <div className={`tlb-row${c.accent ? " tlb-row--accent" : ""}`} key={c.title}>
                          <span className="tlb-row__icon">
                            <Icon size={20} aria-hidden="true" />
                          </span>
                          <div>
                            <h3 className="tlb-row__title">{c.title}</h3>
                            <p className="tlb-row__text">{c.text}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="ft-section ft-section--alt tlb-why">
            <div className="ft-container">
              <SectionHeading
                align="left"
                eyebrow="Why one platform"
                title={
                  <>
                    Every check, <span>one report</span>
                  </>
                }
                subtitle="Lab results live next to your requirements, test cycles and defects, so release decisions are based on the full picture."
              />
              <div className="tlb-panel">
                <div className="tlb-cell">
                  <span className="tlb-cell__icon">
                    <ListChecks size={20} aria-hidden="true" />
                  </span>
                  <h3 className="tlb-cell__title">Same projects, same people</h3>
                  <p className="tlb-cell__text">
                    Labs use the same projects, roles and permissions as the rest of Froth TestOps. There's nothing
                    extra to set up for your team.
                  </p>
                </div>
                <div className="tlb-cell tlb-cell--accent">
                  <span className="tlb-cell__icon">
                    <FrothAiIcon size={20} aria-hidden="true" />
                  </span>
                  <h3 className="tlb-cell__title">AI-written reports</h3>
                  <p className="tlb-cell__text">
                    Turn scan and analysis results into readable reports with FrothAI, ready to share with developers
                    and stakeholders.
                  </p>
                </div>
                <div className="tlb-cell">
                  <span className="tlb-cell__icon">
                    <TrendingUp size={20} aria-hidden="true" />
                  </span>
                  <h3 className="tlb-cell__title">Run on a schedule</h3>
                  <p className="tlb-cell__text">
                    Schedule security scans and code scans so problems are caught between releases, not just before
                    them.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
